import { Hash } from 'lucide-react';
import { useHashtagCatalog } from '../../lib/hashtagCatalog';

/**
 * Pastilles colorées des hashtags d'un dossier (pour listes/pipeline/pilotage).
 * Props :
 *  - ids: string[]            -> liste explicite d'ids (prioritaire si fournie)
 *  - easyPower / chantier: string[] -> fusionnés + dédupliqués si `ids` absent
 *  - max: nombre max de pastilles affichées (puis "+N")
 *  - onTagClick: (id) => void -> si fourni, les pastilles deviennent cliquables
 *    (ex. appliquer le filtre correspondant à la liste). stopPropagation pour ne
 *    pas déclencher l'ouverture du dossier.
 */
export function HashtagChips({ ids, easyPower, chantier, max = 3, className = '', onTagClick }) {
    const catalog = useHashtagCatalog();

    let list = ids;
    if (!list) {
        const seen = new Set();
        list = [];
        [...(easyPower || []), ...(chantier || [])].forEach((id) => {
            if (!seen.has(id)) { seen.add(id); list.push(id); }
        });
    }

    const tags = list.map((id) => ({ id, ...catalog[id] })).filter((t) => t.label);
    if (tags.length === 0) return null;

    const shown = tags.slice(0, max);
    const rest = tags.length - shown.length;
    const base = 'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none text-white';

    return (
        <div className={`flex flex-wrap items-center gap-1 ${className}`} data-testid="hashtag-chips">
            {shown.map((t) => (
                onTagClick ? (
                    <button
                        key={t.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onTagClick(t.id); }}
                        style={{ backgroundColor: t.color }}
                        className={`${base} cursor-pointer transition-transform hover:scale-105`}
                        data-testid={`hashtag-chip-clickable-${t.id}`}
                        title={`Filtrer par #${t.label}`}
                    >
                        <Hash className="w-2.5 h-2.5" />{t.label}
                    </button>
                ) : (
                    <span
                        key={t.id}
                        style={{ backgroundColor: t.color }}
                        className={base}
                        title={`#${t.label}`}
                    >
                        <Hash className="w-2.5 h-2.5" />{t.label}
                    </span>
                )
            ))}
            {rest > 0 && <span className="text-[10px] font-medium text-slate-400">+{rest}</span>}
        </div>
    );
}
